import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, CheckCircle2, AlertCircle } from 'lucide-react';
import { InputField } from '../components/InputField';
import { Button } from '../components/Button';
import { resetPassword } from '../api/auth';
export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!password || password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);
    if (!validate()) return;
    if (!token) {
      setApiError('Invalid or missing reset token. Please request a new reset link.');
      return;
    }
    setIsLoading(true);
    try {
      await resetPassword(token, password);
      setIsSuccess(true);
    } catch (err: any) {
      setApiError(err.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen w-full bg-background md:bg-secondary flex flex-col items-center justify-center p-0 md:p-6 animate-fadeInUp" data-id="element-2084">
      <div className="w-full max-w-[480px] min-h-screen md:min-h-0 bg-background md:rounded-card md:shadow-elevated flex flex-col relative" data-id="element-2085">
        <div className="p-6 pt-12 flex-1" data-id="element-2086">
          {isSuccess ? <div className="flex flex-col items-center justify-center py-12 text-center animate-fadeInUp" data-id="element-2087">
              <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mb-6" data-id="element-2088">
                <CheckCircle2 size={32} className="text-success" data-id="element-2089" />
              </div>
              <h1 className="text-[24px] font-semibold text-text-primary mb-3" data-id="element-2090">
                Password reset successfully!
              </h1>
              <p className="text-[16px] text-text-secondary mb-8 max-w-[280px]" data-id="element-2091">
                You can now sign in with your new password.
              </p>
              <Button onClick={() => navigate('/login')} data-id="element-2092">Back to Login</Button>
            </div> : <>
              <div className="mb-8" data-id="element-2093">
                <h1 className="text-[28px] font-semibold text-text-primary mb-2" data-id="element-2094">
                  Set new password
                </h1>
                <p className="text-[16px] text-text-secondary" data-id="element-2095">
                  Enter your new password below
                </p>
              </div>

              {apiError && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 flex items-center gap-2 animate-slideDown" data-id="element-2096">
                  <AlertCircle size={18} className="text-danger flex-shrink-0" data-id="element-2097" />
                  <span className="text-[14px] text-danger font-medium" data-id="element-2098">
                    {apiError}
                  </span>
                </div>}

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2099">
                <div data-id="element-2100">
                  <label htmlFor="password" className="sr-only" data-id="element-2101">
                    New Password
                  </label>
                  <InputField id="password" name="password" type="password" placeholder="New password" icon={<Lock size={20} data-id="element-2103" />} value={password} onChange={e => {
                setPassword(e.target.value);
                if (errors.password) {
                  setErrors(prev => {
                    const n = {
                      ...prev
                    };
                    delete n.password;
                    return n;
                  });
                }
              }} error={errors.password} data-id="element-2102" />
                </div>

                <div data-id="element-2104">
                  <label htmlFor="confirmPassword" className="sr-only" data-id="element-2105">
                    Confirm Password
                  </label>
                  <InputField id="confirmPassword" name="confirmPassword" type="password" placeholder="Confirm new password" icon={<Lock size={20} data-id="element-2107" />} value={confirmPassword} onChange={e => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) {
                  setErrors(prev => {
                    const n = {
                      ...prev
                    };
                    delete n.confirmPassword;
                    return n;
                  });
                }
              }} error={errors.confirmPassword} data-id="element-2106" />
                </div>

                <div className="pt-4" data-id="element-2108">
                  <Button type="submit" isLoading={isLoading} loadingText="Resetting..." data-id="element-2109">
                    Reset Password
                  </Button>
                </div>
              </form>
            </>}
        </div>
      </div>
    </div>;
}